import { useState } from 'react'
import { Trans } from 'react-i18next'

import { Field, SelectDropdown } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useGetBrandKits } from '@/resolvers/org/brand-kit/query'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { BrandKit } from '@/shared/brand-kit'
import { EVENT_KEYS } from '@/shared/custom-event'
import { Accordion, AccordionItem } from '@/components/Accordion/Accordion'
import { BrandKitColorEditor } from '@/components/BrandKit/BrandKitColorEditor'
import { BrandKitColorPreview } from '@/components/BrandKit/BrandKitColorPreview'
import { BrandKitFontV2 } from '@/components/BrandKit/BrandKitFontV2'
import { BrandKitLogoV2 } from '@/components/BrandKit/BrandKitLogoV2'

enum StyleTab {
  ActiveBrandKit = 'active-brand-kit',
  BrandColors = 'brand-colors',
  BrandLogos = 'brand-logos',
  BrandFonts = 'brand-fonts',
}

/**
 * builder leading-sidebar design panel
 */
export function StylePanel() {
  const { activeBrandKit, handleSetActiveBrandKit } = useAppBrandKitContext()
  const [themeState] = useBuilderColorSchemeV2()
  const [isLoading, setIsLoading] = useState(false)

  // @note: we fetch brandkits separately here, since its removed from AppBrandKitContext
  const brandkitsQuery = useGetBrandKits()
  const brandKits = brandkitsQuery.data || []

  /**
   * Set active brand kit and reset current theme index.
   */
  const handleChangeActiveBrandKit = async (opt: BrandKit) => {
    setIsLoading(true)
    try {
      await handleSetActiveBrandKit(opt.id, '0')
      window.dispatchEvent(new CustomEvent(EVENT_KEYS.RELOAD_PREVIEW_FRAME))
      toasted('Active brand kit updated')
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }

    // todo: reset navbar settings logo
    // setNavbarSettings((prev) => ({
    //   ...prev,
    //   items: prev.items.map((item) => {
    //     if (item.logo.value) {
    //       if (opt.logos.length > 0) {
    //         item.logo.value = opt.logos[0]
    //         item.logo.label = opt.name
    //       } else {
    //         item.logo.value = ''
    //         item.logo.label = ''
    //       }
    //     }
    //     return item
    //   }),
    // }))
  }

  return (
    <div className='design-panel mk-builder-panel'>
      <div className='p-4'>
        <div className='uppercase pt-2'>
          <Trans>Style</Trans>
        </div>
      </div>

      <Accordion openMultiple={false} initialActiveItems={[StyleTab.BrandColors]}>
        <AccordionItem itemId={StyleTab.ActiveBrandKit} title={<Trans>Active Brand Kit</Trans>} isLoading={isLoading}>
          <Field isVertical className='relative'>
            <SelectDropdown
              onChange={handleChangeActiveBrandKit}
              value={brandKits?.find((bk) => bk.id === activeBrandKit?.id)}
              options={brandKits}
              optionLabel={(opt) => opt.name}
              renderOption={(opt) => {
                return (
                  <div className='w-[95%]'>
                    <div className='pb-1'>{opt?.name}</div>
                    <div className='pb-1'>
                      <BrandKitColorPreview
                        className='h-4 rounded-xs'
                        brandKit={opt}
                        defaultHeight={false}
                        mode={themeState.current}
                      />
                    </div>
                  </div>
                )
              }}
              disabled={isLoading}
            />
          </Field>
        </AccordionItem>

        <AccordionItem itemId={StyleTab.BrandColors} title={<Trans>Brand Color Schemes</Trans>}>
          <BrandKitColorEditor />
        </AccordionItem>

        <AccordionItem itemId={StyleTab.BrandLogos} title={<Trans>Brand Logos</Trans>}>
          <BrandKitLogoV2 withPadding={false} />
        </AccordionItem>

        <AccordionItem itemId={StyleTab.BrandFonts} title={<Trans>Brand Fonts</Trans>}>
          <BrandKitFontV2 />
        </AccordionItem>
      </Accordion>
    </div>
  )
}
